//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { useInstagramMessagesAccess } from '@account-settings/hooks/useInstagramMessagesAccess';
import { getAccountNames } from '@account-settings/utils/instagramMessagesAccessStatusUtils';
import { Alert } from '@components/atoms/alert/Alert';
import { useHasInstagramManageMessagesFeatureAndPermissions } from '@uia/rights/hooks/useHasInstagramManageMessagesFeatureAndPermissions';
import { DmRightsRequestSetupGuideLink } from '@uia/rights/info/DmRightsRequestSetupGuideLink';
import { pluralize } from '@utils/stringUtils';

export const DmRightsRequestConfigInfo = () => {
  const {
    hasInstagramManageMessagesFeatureAndPermissions,
    isFetchingInstagramManageMessagesFeatureAndPermissions,
  } = useHasInstagramManageMessagesFeatureAndPermissions();

  const {
    data: { accountsWithAccess = [], accountsWithoutAccess = [] } = {},
    isError: isMessagesAccessStatusError,
    isLoading: isMessagesAccessStatusLoading,
  } = useInstagramMessagesAccess();

  const noConnectedAccountsStatus =
    accountsWithAccess.length === 0 && accountsWithoutAccess.length === 0;

  if (
    isFetchingInstagramManageMessagesFeatureAndPermissions ||
    isMessagesAccessStatusLoading ||
    // we can safely ignore errors from useInstagramMessagesAccess entirely if hasInstagramManageMessagesFeatureAndPermissions is false.
    // because the hasInstagramManageMessagesFeatureAndPermissions is a pre requisite for getting the messages access status and the api is expected to throw an error in that case.
    (hasInstagramManageMessagesFeatureAndPermissions &&
      (isMessagesAccessStatusError || noConnectedAccountsStatus))
  ) {
    return null;
  }

  const noAccountHasAccess = accountsWithAccess.length === 0;
  const allAccountsHaveAccess = accountsWithoutAccess.length === 0;
  const accountUserNamesWithoutAccess = getAccountNames(accountsWithoutAccess);

  if (
    hasInstagramManageMessagesFeatureAndPermissions &&
    allAccountsHaveAccess
  ) {
    return null;
  }

  if (!hasInstagramManageMessagesFeatureAndPermissions || noAccountHasAccess) {
    return (
      <StyledAlert type='warning'>
        To request content rights via Instagram Direct Message, you need
        to&nbsp;
        {/* TODO: PD-272415 Convert the "refresh" text to a Link to settings page */}
        {!hasInstagramManageMessagesFeatureAndPermissions
          ? 'refresh your Facebook account connection and '
          : ''}
        enable message access for your Instagram account(s). Follow the setup
        instructions to get started seamlessly.&nbsp;
        <DmRightsRequestSetupGuideLink text='Setup' />
      </StyledAlert>
    );
  } else {
    return (
      <StyledAlert type='warning'>
        To request rights via DM, please use an Instagram account with messaging
        access enabled. The following&nbsp;
        {pluralize(
          accountsWithoutAccess.length,
          'account',
          'accounts',
          false,
        )}{' '}
        do not have messaging access enabled: {accountUserNamesWithoutAccess}
        &nbsp;
        <DmRightsRequestSetupGuideLink text='Learn more' />
      </StyledAlert>
    );
  }
};

const StyledAlert = styled(Alert)`
  margin-bottom: 0;
`;
